/* ============================================================
   FLOOF .. CART

   Opens the drawer, mutates the cart, and swaps in server-rendered HTML.
   IT NEVER COMPUTES A PRICE. Every total, discount and currency string comes
   back from Liquid via the Section Rendering API, so the client cannot disagree
   with the server about what anything costs.

   EVERY LISTENER IS DELEGATED ON document, and that is deliberate rather than
   lazy. Three payoffs:
     1. It works with the existing nav, the product form, the collection grid and
        anything added later, without any of them knowing this file exists.
     2. It survives the drawer being replaced wholesale after every mutation.
        A listener bound to the drawer element would die with it.
     3. Quick-add buttons that do not exist yet get the behaviour for free.

   WHAT IT HOOKS
     [data-cart-open]           -> open
     a[href$="/cart"]           -> open instead of navigating (plain clicks only),
                                   EXCEPT inside the drawer .. see the note there
     [data-cart-close]          -> close
     form[action*="/cart/add"]  -> AJAX add, then open .. UNLESS the form sits
                                   inside a Horizon <product-form-component>,
                                   which has its own handler. Two handlers on one
                                   submit means the item is added twice.
     [data-cart-qty]            -> change line quantity (data-line, data-qty)

   ------------------------------------------------------------
   !! A SECTION RENDERING ID IS **NOT** THE KEY FROM THE TEMPLATE JSON, AND
      GETTING THIS WRONG FAILS WITH A 200.

   templates/cart.json keys its section "main". The id Shopify actually renders is
   `template--27106748596519__main`, and the id for a section inside a section
   GROUP is `sections--27106744172839__floof_nav`. Ask the API for the bare key and
   you get:

       GET /?sections=main        ->  200  {"main": null}

   Not a 404. Not an error. A null, which every reasonable guard treats as "nothing
   to do" .. so the request silently accomplishes nothing. This file used to ask for
   `main` and for `floof_nav`, and the consequences were exactly as invisible as
   that implies: the DRAWER updated (its id really is `floof-cart-drawer`, because a
   statically rendered section has no template prefix) while the CART PAGE behind it
   went stale. Quantity changes on /cart mutated the server and changed nothing on
   screen.

   That was not only a display bug. snippets/floof-cart-lines.liquid addresses lines
   by 1-BASED INDEX, which its own comment says is safe "ONLY because every mutation
   re-renders this whole list from the server". On a page that never re-rendered,
   the indexes went stale: remove line 1, then touch what still looks like line 2,
   and you edit the wrong product.

   THE RULE: never hardcode a section id. Read it off the DOM, where Shopify has
   already written the real one. sectionIdOf() below is the only place that knows
   the `shopify-section-` prefix.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  var DRAWER = 'floof-cart-drawer';
  var PREFIX = 'shopify-section-';
  var busy = false;

  function dlg() { return document.getElementById('FloofCart'); }
  function err(msg) {
    var el = document.querySelector('[data-cart-err]');
    if (el) el.textContent = msg || '';
  }

  /* ---------------------------------------------------------
     SECTION IDS, READ FROM THE DOM

     The cart page marks itself with data-cart-page in sections/floof-cart.liquid,
     on the section root rather than on .f-page-cart .. because .f-page-cart only
     exists when the bag has something in it. Removing the last line has to be able
     to re-render the page INTO its empty state, so the marker must survive the
     transition between the two.
     --------------------------------------------------------- */

  function hostOf(el) {
    if (!el || !el.closest) return null;
    return el.closest('[id^="' + PREFIX + '"]');
  }

  function sectionIdOf(el) {
    var host = hostOf(el);
    return host ? host.id.slice(PREFIX.length) : null;
  }

  function pageHost() { return hostOf(document.querySelector('[data-cart-page]')); }

  function sectionList() {
    var ids = [DRAWER];
    var host = pageHost();
    if (host) ids.push(host.id.slice(PREFIX.length));
    return ids.join(',');
  }

  /* ---------------------------------------------------------
     OPEN / CLOSE
     A dialog cannot transition out .. display:none applies immediately on
     close(). So the [closing] attribute drives the outbound animation and the
     real close() waits for it. The timeout is the safety net: if the transition
     is interrupted or never fires, the drawer must still close.
     --------------------------------------------------------- */

  function open() {
    var d = dlg();
    if (!d || d.open) return;
    d.removeAttribute('closing');
    d.showModal();
  }

  function close() {
    var d = dlg();
    if (!d || !d.open) return;
    d.setAttribute('closing', '');

    var done = function () {
      d.removeAttribute('closing');
      if (d.open) d.close();
    };
    var guard = setTimeout(done, 600);

    d.addEventListener('transitionend', function onEnd(e) {
      if (e.target !== d) return;
      clearTimeout(guard);
      d.removeEventListener('transitionend', onEnd);
      done();
    });
  }

  /* ---------------------------------------------------------
     FOCUS ACROSS A SWAP

     Re-rendering from the server means the element the visitor is standing on is
     DESTROOYED. Focus falls to <body>, and for a keyboard or screen-reader user that
     is the end of the interaction: to press + twice they would have to tab back in
     from the top of the document every time.

     So the control is described before the swap and found again after. The
     description cannot include data-qty .. that is the value the button SETS, and it
     is different in the fresh markup by definition (the + that said 3 now says 4).
     What is stable is the line and the button's ROLE, and the role is positional:
     the minus is the first child of .f-cart__qty, the plus is the last.

     THE 'rm' BRANCH IS NOW UNREACHABLE and kept deliberately. The separate Remove
     link was removed on 2026-08-24 .. the down button already deletes the line at a
     quantity of one and now shows a bin glyph there .. so nothing carries
     .f-cart__rm any more. The positional test still tells the bin and the plus
     apart, because the bin is still the first child. If a Remove link ever returns,
     this is already wired for it.

     If the line itself is gone .. quantity 0 .. there is nothing to return to, so
     focus goes to the nearest sensible thing that still exists rather than nowhere.
     --------------------------------------------------------- */

  function roleOf(el) {
    if (el.classList.contains('f-cart__rm')) return 'rm';
    return el.previousElementSibling ? 'inc' : 'dec';
  }

  function focusKey() {
    var el = document.activeElement;
    if (!el || !el.closest) return null;
    var btn = el.closest('[data-cart-qty]');
    /* Safari does not focus a button on click, so activeElement is <body> and there
       is nothing to restore. That is correct .. only restore what was really held. */
    if (!btn) return null;
    return {
      line: btn.getAttribute('data-line'),
      role: roleOf(btn),
      inDrawer: !!btn.closest('#FloofCart')
    };
  }

  function restoreFocus(key) {
    if (!key) return;
    var scope = key.inDrawer ? dlg() : document;
    if (!scope) return;

    var same = scope.querySelectorAll('[data-cart-qty][data-line="' + key.line + '"]');
    var target = null;

    for (var i = 0; i < same.length; i++) {
      if (roleOf(same[i]) === key.role) { target = same[i]; break; }
    }
    if (!target && same.length) target = same[0];

    /* the line was removed: land somewhere real rather than on <body> */
    if (!target) {
      target =
        scope.querySelector('[data-cart-qty]') ||
        scope.querySelector('.f-cart__empty .f-btn') ||
        (key.inDrawer ? scope.querySelector('[data-cart-close]') : null);
    }

    if (target && target.focus) target.focus();
  }

  /* ---------------------------------------------------------
     SWAP

     The drawer swaps its INNER content, not the whole section. Replacing the
     <dialog> itself would remove an open dialog from the DOM, and re-opening the
     replacement re-runs the slide-in .. a visible flinch every time you tap +.
     Keeping the element and changing its guts leaves the open state untouched.

     The cart page has no dialog, so its whole section element is replaced. That is
     also what lets the page cross between the filled and empty states: the marker
     lives on the section root, so it comes back either way.
     --------------------------------------------------------- */

  function swapDrawer(html) {
    var d = dlg();
    if (!d || !html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var fresh = tmp.querySelector('.f-cart__in');
    var live = d.querySelector('.f-cart__in');
    if (fresh && live) live.replaceWith(fresh);
  }

  function swapPage(host, html) {
    if (!host || !html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    /* the API returns the section WRAPPER, so this is the new #shopify-section-… */
    var fresh = tmp.firstElementChild;
    if (fresh) host.replaceWith(fresh);
  }

  /* THE BAG BADGE IS NOT THIS FILE'S JOB, and it used to think it was.

     sections/floof-nav.liquid owns it: watchCart() there wraps window.fetch, so any
     request to /cart/add|change|update|clear dispatches `cart:updated`, and the nav
     re-reads /cart.js and writes its own badge. That covers every mutation however
     it is performed, including the ones in this file, and costs one ~500 byte JSON
     request.

     What used to be here was a syncBag() that re-rendered the ENTIRE 41KB nav
     section to copy one number across. It never ran: it asked for `?sections=
     floof_nav`, got {"floof_nav": null}, and returned at its own guard. Both files
     described it as deliberate belt-and-braces that agreed with the nav's mechanism.
     One of the two was dead, and saying so in two places is how it stayed dead. */

  function applySections(data) {
    if (!data || !data.sections) return;
    swapDrawer(data.sections[DRAWER]);

    var host = pageHost();
    if (host) {
      var id = host.id.slice(PREFIX.length);
      if (data.sections[id]) swapPage(host, data.sections[id]);
    }
  }

  function setBusy(on) {
    busy = on;
    var d = dlg();
    if (d) d.classList.toggle('is-busy', on);
    var p = document.querySelector('[data-cart-page]');
    if (p) p.classList.toggle('is-busy', on);
  }

  /* ---------------------------------------------------------
     MUTATIONS .. both endpoints accept `sections`, so one round trip does the
     change AND returns the fresh HTML. Two requests would let the drawer paint a
     stale total for a frame.

     ------------------------------------------------------------------
     !! BOTH OF THESE MUST CHECK r.ok, AND change() DID NOT. THAT WAS A BUG, AND
     IT PRESENTED AS "the + button does nothing".

     Reported 2026-08-24 against a Rose Tile Cap in Washed sage, which has an
     inventory quantity of exactly 1 with an inventory policy of DENY. Pressing +
     asked for 2, and Shopify correctly refused:

         POST /cart/change.js   ->  422   {"description": "You can only add …"}

     A 422 STILL RETURNS PARSEABLE JSON, which is what made this invisible.
     change() did `.then(function (r) { return r.json(); })` with no look at r.ok,
     so the error body sailed straight into applySections(). That function guards on
     `data.sections` .. which an error body does not have .. and returned silently.
     So: no swap, no message, nothing in the console, and a + that appeared dead.
     The .catch() never fired either, because nothing threw.

     add() had it right all along and change() was written to a different shape,
     which is the whole lesson: TWO FUNCTIONS DOING THE SAME KIND OF WORK MUST
     HANDLE FAILURE THE SAME WAY. The asymmetry is the bug.

     USE SHOPIFY'S OWN WORDING. `description` names the product and the real limit
     ("You can only add 1 Rose Tile Cap - Washed sage to the cart"), which the
     client cannot know and must never guess.

     snippets/floof-cart-lines.liquid ALSO disables the + when the bag already holds
     every unit in stock, so in the common case this message is a safety net rather
     than the first line of defence. It still has to exist: stock can change between
     the render and the tap, and a 422 has other causes.
     ------------------------------------------------------------------ */

  function change(line, qty) {
    if (busy) return;
    setBusy(true);
    err('');

    var key = focusKey();

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty, sections: sectionList() })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok) {
          err(res.j.description || res.j.message || 'Could not update the bag.');
          return;
        }
        applySections(res.j);
        restoreFocus(key);
      })
      .catch(function () { err('Could not update the bag. Try again.'); })
      .finally(function () { setBusy(false); });
  }

  function add(form) {
    if (busy) return;
    setBusy(true);
    err('');

    var body = new FormData(form);
    body.append('sections', sectionList());

    fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok) {
          /* Shopify returns 422 with a human description .. "You can only add 2
             of Floof Body Pillow to the cart." Use THEIR wording, not ours. */
          err(res.j.description || res.j.message || 'Could not add that.');
          open();
          return;
        }
        applySections(res.j);
        open();
      })
      .catch(function () { err('Could not add that. Try again.'); })
      .finally(function () { setBusy(false); });
  }

  /* ---------------------------------------------------------
     DELEGATION
     --------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    if (t.closest('[data-cart-close]')) { close(); return; }

    var step = t.closest('[data-cart-qty]');
    if (step) {
      e.preventDefault();
      /* a disabled + is inert by definition, but the delegated listener would still
         see the click if the browser let it through .. guard rather than trust */
      if (step.disabled || step.getAttribute('aria-disabled') === 'true') return;
      change(parseInt(step.getAttribute('data-line'), 10), parseInt(step.getAttribute('data-qty'), 10));
      return;
    }

    if (t.closest('[data-cart-open]')) { e.preventDefault(); open(); return; }

    /* ---------------------------------------------------------------
       THE BAG LINK .. "any link to /cart opens the drawer instead of navigating".

       Modified and middle clicks are left alone so "open in new tab" still works,
       and it only intercepts when a drawer actually exists .. so /cart stays
       reachable if this section is ever removed.

       !! IT MUST NOT INTERCEPT A CART LINK THAT IS ITSELF INSIDE THE DRAWER, AND
       THAT EXCLUSION IS THE WHOLE POINT OF THIS COMMENT.

       Reported 2026-08-23: "View the full bag" in the drawer footer did nothing.
       That link is `<a href="/cart">`, so this branch caught it, called
       preventDefault() and then open() .. on a drawer that was already open. open()
       early-returns on `d.open`, so the result was a button that swallowed the tap
       and produced no navigation and no visible change. Nothing errored, nothing
       logged, and the ONLY escape from the drawer to the full cart page was gone.

       The intent of this branch is "a cart link ELSEWHERE on the page should open
       the drawer rather than leave it". A cart link INSIDE the drawer means the
       opposite by definition: the visitor is already looking at the drawer and is
       asking for the full page. Intercepting it can never be right.

       WHY NOT JUST DELETE THE BRANCH: the nav's bag icon carries
       [data-cart-open] and is handled above, so this looks redundant .. but it also
       covers cart links with no marker attribute, such as a "Cart" entry in a
       merchant-edited footer menu, or anything a Horizon template renders. Those
       should still open the drawer. So the branch stays and the drawer is carved
       out of it.

       #FloofCart, not `dialog`, matching focusKey() above .. it is the id this file
       already treats as the drawer, and Horizon's own modals were removed from the
       layout so a generic `dialog` check would be looser for no benefit.
       --------------------------------------------------------------- */
    var bag = t.closest('a[href$="/cart"]');
    if (
      bag &&
      !bag.closest('#FloofCart') &&
      dlg() &&
      e.button === 0 &&
      !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
    ) {
      e.preventDefault();
      open();
      return;
    }

    /* backdrop: a click on the dialog itself, outside the panel */
    var d = dlg();
    if (d && d.open && t === d) close();
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    /* Horizon owns its own product forms .. adding twice is worse than not
       opening the drawer */
    if (form.closest('product-form-component')) return;
    e.preventDefault();
    add(form);
  });

  /* Escape closes natively, but that skips the outbound animation, so take it
     over. cancel fires before the dialog closes itself. */
  document.addEventListener('cancel', function (e) {
    var d = dlg();
    if (d && e.target === d) { e.preventDefault(); close(); }
  });

  /* other code can ask for the drawer without importing anything */
  document.addEventListener('floof:cart:open', open);
  document.addEventListener('floof:cart:refresh', function () {
    fetch('/?sections=' + sectionList())
      .then(function (r) { return r.json(); })
      .then(function (s) { applySections({ sections: s }); })
      .catch(function () {});
  });
})();